import { ipaToFeatures, IpaType, isTone } from "../phonemes/ipa";
import { PhonemeItem, segmentPhonemes } from "../phonemes/segmentPhonemes";
import { convertTones } from "./convertTones";
import { fixedPhonemeLengths, ipaToSynthSymbols, millisecondsToIpa, plusExtraShort, shortestInMilliseconds, symbolToMilliseconds } from "./exact";
import { ipaToSynth } from "./ipaToSynth";

/** make sure all tones are handled & phoneme lengths target the proper phonemes using synth lengths of 0-9 */
export function convertPlusSymbols(ipa: IpaType, defaultDiphthongMS?: number): IpaType {
  const phonemes = segmentPhonemes(ipa, 'ipaPlus').phonemes;
  const withTones = convertTones(phonemes);
  const withLength = convertLength(withTones);
  const results = addDefaultDiphthong(withLength, defaultDiphthongMS);
  return results.map(p => p.phoneme).join('');
}

/**
 * take ipa that might contain numbers representing phoneme lengths
 * and possibly break apart numbers based on whether or not diphthongs, etc.
 * are represented in the synth encoding,
 * e.g., /aɪ̯8/ stays because the diphthong is supported, but /aʉ̯8/ > /a8ʉ0/,
 * supports input that's either synth lengths of 0-9 or milliseconds,
 * output is in synth lengths of 0-9
 * (note that adding a 50ms extra-short vowel to another vowel typically
 * doesn't change the overall length much)
 */
function convertLength(phonemes: PhonemeItem[]): PhonemeItem[] {
  let results: PhonemeItem[] = [];
  for (let i = 0; i < phonemes.length; i++) {
    const p = phonemes[i];
    if (p.meta === undefined) {
      const features = ipaToFeatures(p.phoneme);
      if (features?.includes('vowel')) {
        const result = getComboWithLength(phonemes, i);
        if (result !== null) {
          // diphthong, vowel + approximate, etc.
          const group = possiblySplit(result.pieces, result.number);
          results.push(...group);
          i += result.count - 1;
          continue;
        }
      }
      // single phoneme, if next symbol is length, use it
      if (isLength(phonemes[i + 1])) {
        const len = numberToSynthLength(phonemes[i + 1].phoneme);
        const withLen = getIpaAndLength(p, len);
        results.push(...withLen);
        i++;
        continue;
      }
    }
    results.push(p);
  }
  return results;
}

/** check if this is a length number */
function isLength(pi: PhonemeItem): boolean {
  if (pi === undefined) {
    return false;
  }
  const char = pi.phoneme.charAt(0);
  return '0' <= char && char <= '9';
}

/** combine ipa+length, possibly with modifications needed for the ipa->synth rules */
function getIpaAndLength(phoneme: PhonemeItem, length: string | null): PhonemeItem[] {
  const ipa = phoneme.phoneme;
  if (length === '0') {
    if (ipa === 'ə' || ipa === 'ə̆') {
      // because /ə̆/ > [@-], which espeak has explicit checks for
      return [{ phoneme: 'ə̆' }];
    }
    if (isTone(ipa)) {
      return [phoneme]; // don't add length to a tone
    }
    const features = ipaToFeatures(ipa);
    if (features?.includes('non-syllabic')) {
      // e.g., return /y̯/ w/out length because the rules know if it should be [j] or [y0]
      return [phoneme];
    }
  }
  const stripped = { phoneme: strip(ipa) };
  return length === null ? [stripped] : [stripped, { phoneme: length }];
}

/** remove diacritics that are no longer needed */
function strip(ipa: IpaType): IpaType {
  // remove length modifiers & non-syllabic symbols
  return ipa.replace(/\u02D0|\u02D1|\u0306|\u032F|\u0311/g, '');
}

/**
 * if this is a vowel-combo with a length, separate out the combo & the length,
 * else null,
 * assumes phonemes[current] is a vowel
 */
function getComboWithLength(
  phonemes: PhonemeItem[], current: number
): null | { pieces: PhonemeItem[], count: number, number: string } {
  // check for [tone, non-syllabic, length]
  if (current + 2 < phonemes.length && isTone(phonemes[current + 1].phoneme)
    && isNonSyllabic(phonemes[current + 2]) && isLength(phonemes[current + 3])
  ) {
    const slice = phonemes.slice(current, current + 3);
    return { pieces: slice, count: 4, number: phonemes[current + 3].phoneme };
  }
  // check for 1 or more non-syllabics followed by a length
  for (let i = current + 1; i < phonemes.length; i++) {
    if (isNonSyllabic(phonemes[i])) {
      if (isLength(phonemes[i + 1])) {
        const slice = phonemes.slice(current, i + 1);
        return { pieces: slice, count: i - current + 2, number: phonemes[i + 1].phoneme };
      }
    } else {
      return null;
    }
  }
  return null;
}
/** check for non-syllabic vowel or approximant */
function isNonSyllabic(p: PhonemeItem): boolean {
  if (p === undefined) {
    return false;
  }
  const features = ipaToFeatures(p.phoneme);
  if (features === undefined) {
    return false;
  }
  // if either a non-syllabic vowel or approximant...
  return ((features.includes('vowel') && features.includes('non-syllabic'))
    || (!features.includes('syllabic')  // e.g., the 3 in /ɝɫ̩3/ only refers to the /ɫ̩/
      && (features.includes('approximant') || features.includes('lateral-approximant'))));
}

/** if <10, as-is, else it's milliseconds so convert to synth's 0-9 */
function numberToSynthLength(number: string): string {
  const total = Number.parseInt(number);
  return total < 10 && number.length === 1 ? number : millisecondsToIpa(total);
}

/**
 * if vowel-combo is supported by the synth encoding, return as-is,
 * else break the length across the vowels
 */
function possiblySplit(slice: PhonemeItem[], number: string): PhonemeItem[] {
  // if it has a direct representation, use as-is
  const thong = slice.map(p => p.phoneme).join('');
  if (ipaToSynthSymbols[thong] !== undefined) {
    const length = numberToSynthLength(number);
    return slice.concat({ phoneme: length });  // supported -> as-is
  }

  if (slice.length >= 3) {
    // see if initial pair has direct representation
    const thong2 = `${slice[0].phoneme}${slice[1].phoneme}`;
    if (ipaToSynthSymbols[thong2] !== undefined) {
      const slice2 = [{ phoneme: thong2 }].concat(slice.slice(2));
      const lengths = getLengths(slice2, number);
      const results = slice2.flatMap((p, i) =>
        i === 0 && lengths[0] !== null
          ? [slice[0], slice[1], { phoneme: lengths[0] }]
          : i === 0 ? [slice[0], slice[1]]
            : getIpaAndLength(p, lengths[i]));
      return results;
    }
  }

  // unsupported, break apart
  const lengths = getLengths(slice, number);
  const results = slice.flatMap((p, i) => getIpaAndLength(p, lengths[i]));
  return results;
}

/** figure out how long each phoneme should be from 0-9, or null for implicit length */
function getLengths(slice: PhonemeItem[], number: string): (string | null)[] {
  const fixedLens = slice.map(p => FixedLengths.getLength(p.phoneme));
  const lengthsMs = computeLengthsMs(number, fixedLens);
  return lengthsMs.map((l, i) => fixedLens[i] ? null : millisecondsToIpa(l));
}

/** compute the lengths of each piece in milliseconds */
function computeLengthsMs(number: string, fixedLens: number[]): number[] {
  // get total length in ms
  const raw = Number.parseInt(number);
  const totalMs = raw >= 10 ? raw // already in ms
    : raw >= 0 ? symbolToMilliseconds[raw]  // 0-9 => ms
      : shortestInMilliseconds; // default if invalid

  // get lengths of everything except the initial vowel, treating extra-short vowels as 10ms
  const lengthsMs = fixedLens.map((len, i) => i === 0 ? 0 : len !== 0 ? len : plusExtraShort);
  // figure out length of initial vowel
  const fixedSumMs = lengthsMs.reduce((sum, len) => sum + len);
  const leftOver = totalMs > fixedSumMs ? totalMs - fixedSumMs : shortestInMilliseconds;
  lengthsMs[0] = leftOver;
  return lengthsMs;
}


/** figure out how long a phoneme is, designed for approximants */
class FixedLengths {
  private readonly phoneToLen: Record<IpaType, number> = {};
  private static instance: FixedLengths | null = null;

  /** get fixed phoneme length if ipa converts to synth in 'fixedPhonemeLengths' */
  static getLength(ipa: IpaType): number {
    if (this.instance === null) {
      this.instance = new FixedLengths();
    }
    return this.instance.findLength(ipa);
  }

  private findLength(ipa: IpaType): number {
    // check cache
    const len = this.phoneToLen[ipa];
    if (len !== undefined) {
      return len;
    }

    // ipa -> synth
    const synth = ipaToSynth(ipa);
    const check = fixedPhonemeLengths[synth.speak];
    return check ?? 0;
  }
}



/** add default length to any diphthongs without a length */
function addDefaultDiphthong(input: PhonemeItem[], defaultDiphthongMS?: number): PhonemeItem[] {
  if (defaultDiphthongMS === undefined) {
    return input;
  }

  // first map out V/vowels, N/non-syllabic-vowels, L/length, -/other
  const types = input.map(p => {
    if (p.meta === undefined) {
      if (isNonSyllabic(p)) {
        return 'N';
      } else {
        const features = ipaToFeatures(p.phoneme);
        if (features?.includes('vowel')) {
          return 'V';
        } else if (features?.includes('length')) {
          return 'L';
        }
      }
    }
    return '-';
  });

  // now add length to all diphthongs without length
  const output: PhonemeItem[] = [];
  const len = { phoneme: defaultDiphthongMS.toString() };
  for (let i = 0; i < input.length; i++) {
    let addAtEnd = (() => {
      if (types[i - 1] === 'V' && types[i] === 'N' && types[i + 1] !== 'L') {
        // found VN(!L), if VN is supported by synth, do VNL, else do VLN
        const thong = `${input[i - 1].phoneme}${input[i].phoneme}`;
        return ipaToSynthSymbols[thong] ? 'after' : 'now';
      }
      return null;
    })();
    if (addAtEnd === 'now') output.push(len);
    output.push(input[i]);
    if (addAtEnd === 'after') output.push(len);
  }
  return output;
}
